import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DesktopShell } from '../components/DesktopShell';
import { TopBar } from '../components/TopBar';
import { Card } from '../components/Card';
import { Btn } from '../components/Btn';
import { Pill } from '../components/Pill';
import { Modal } from '../components/Modal';
import { Avatar } from '../components/Avatar';
import { Ic } from '../components/Icon';
import { AdminTabs } from '../components/AdminTabs';
import { IconAction } from '../components/IconAction';
import { adminApi, type AdminUserDto, type AdminUserStatus } from '../api/admin';
import type { HttpError } from '../api/client';
import { formatDate } from '../lib/date';

type ActionKind = 'promote' | 'demote' | 'block' | 'unblock' | 'resend' | 'delete';

const STATUS_META: Record<AdminUserStatus, { label: string; color: 'green' | 'live' | 'gray' }> = {
  ACTIVE: { label: 'Aktywny', color: 'green' },
  BLOCKED: { label: 'Zablokowany', color: 'live' },
  PENDING_VERIFICATION: { label: 'Oczekuje', color: 'gray' },
};

function StatusPill({ status }: { status: AdminUserStatus }) {
  const meta = STATUS_META[status];
  return <Pill color={meta.color}>{meta.label}</Pill>;
}

interface PendingAction {
  user: AdminUserDto;
  kind: ActionKind;
}

const ACTION_META: Record<ActionKind, { title: string; cta: string; danger: boolean; busy: string; description: (u: AdminUserDto) => React.ReactNode }> = {
  promote: {
    title: 'Nadać uprawnienia administratora?',
    cta: 'Nadaj admina',
    busy: 'Nadawanie…',
    danger: false,
    description: (u) => (
      <p style={{ margin: 0 }}>
        <strong>{u.nick}</strong> otrzyma pełne uprawnienia administratora — dostęp do panelu i zarządzanie innymi
        użytkownikami.
      </p>
    ),
  },
  demote: {
    title: 'Odebrać uprawnienia administratora?',
    cta: 'Odbierz admina',
    busy: 'Odbieranie…',
    danger: false,
    description: (u) => (
      <p style={{ margin: 0 }}>
        <strong>{u.nick}</strong> straci dostęp do panelu administratora i będzie zwykłym użytkownikiem.
      </p>
    ),
  },
  block: {
    title: 'Zablokować użytkownika?',
    cta: 'Zablokuj',
    busy: 'Blokowanie…',
    danger: true,
    description: (u) => (
      <>
        <p style={{ margin: '0 0 8px' }}>
          Użytkownik <strong>{u.nick}</strong> nie będzie mógł się zalogować, dopóki go nie odblokujesz.
        </p>
        <p style={{ margin: 0, color: '#5b6578', fontSize: 13 }}>
          Jego dane (typy, członkostwa) pozostają nietknięte.
        </p>
      </>
    ),
  },
  unblock: {
    title: 'Odblokować użytkownika?',
    cta: 'Odblokuj',
    busy: 'Odblokowywanie…',
    danger: false,
    description: (u) => (
      <p style={{ margin: 0 }}>
        <strong>{u.nick}</strong> odzyska możliwość logowania.
      </p>
    ),
  },
  resend: {
    title: 'Wysłać ponownie link aktywacyjny?',
    cta: 'Wyślij link',
    busy: 'Wysyłanie…',
    danger: false,
    description: (u) => (
      <p style={{ margin: 0 }}>
        Na adres <strong>{u.email}</strong> zostanie wysłany nowy link aktywacyjny.
      </p>
    ),
  },
  delete: {
    title: 'Usunąć konto użytkownika?',
    cta: 'Usuń konto',
    busy: 'Usuwanie…',
    danger: true,
    description: (u) => (
      <>
        <p style={{ margin: '0 0 8px' }}>
          Konto <strong>{u.nick}</strong> ({u.email}) zostanie nieodwracalnie usunięte.
        </p>
        <p style={{ margin: 0, color: '#5b6578', fontSize: 13 }}>
          Wraz z nim znikną wszystkie typy, członkostwa, zaproszenia i powiadomienia tego użytkownika.
          Jeśli jest właścicielem pokojów, najpierw przekaż własność lub usuń pokoje.
        </p>
      </>
    ),
  },
};

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['admin-users'], queryFn: adminApi.listUsers });
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  const onError = (err: unknown) => {
    setPending(null);
    setActionError((err as HttpError)?.body?.message ?? 'Akcja nie powiodła się.');
  };
  const onSuccess = () => {
    setPending(null);
    invalidate();
  };

  const promote = useMutation({ mutationFn: adminApi.promoteUser, onSuccess, onError });
  const demote = useMutation({ mutationFn: adminApi.demoteUser, onSuccess, onError });
  const block = useMutation({ mutationFn: adminApi.blockUser, onSuccess, onError });
  const unblock = useMutation({ mutationFn: adminApi.unblockUser, onSuccess, onError });
  const resend = useMutation({
    mutationFn: adminApi.resendUserVerification,
    onSuccess: () => {
      setPending(null);
      setResendSuccess(true);
      invalidate();
    },
    onError,
  });
  const del = useMutation({ mutationFn: adminApi.deleteUser, onSuccess, onError });

  const mutations: Record<ActionKind, ReturnType<typeof useMutation<AdminUserDto | void, Error, string>>> = {
    promote: promote as never,
    demote: demote as never,
    block: block as never,
    unblock: unblock as never,
    resend: resend as never,
    delete: del as never,
  };

  const isPending = pending ? mutations[pending.kind].isPending : false;
  const meta = pending ? ACTION_META[pending.kind] : null;

  const confirmPending = () => {
    if (!pending) return;
    setActionError(null);
    setResendSuccess(false);
    mutations[pending.kind].mutate(pending.user.id);
  };

  const users = list.data ?? [];
  const adminCount = users.filter((u) => u.role === 'ADMIN').length;

  return (
    <DesktopShell>
      <TopBar title="Panel admina" subtitle="Zarządzanie użytkownikami" />
      <AdminTabs />
      <div className="responsive-pad" style={{ flex: 1, padding: '0 36px 40px' }}>
        {actionError && (
          <Card padding={12} style={{ background: 'rgba(208,70,60,.08)', color: '#8a2a22', marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13 }}>{actionError}</span>
              <button
                onClick={() => setActionError(null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#8a2a22' }}
              >
                <Ic.x size={16} />
              </button>
            </div>
          </Card>
        )}

        {resendSuccess && (
          <Card padding={12} style={{ background: 'rgba(31,157,85,.1)', color: '#0f6b34', marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13 }}>Wysłano nowy link aktywacyjny.</span>
              <button
                onClick={() => setResendSuccess(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#0f6b34' }}
              >
                <Ic.x size={16} />
              </button>
            </div>
          </Card>
        )}

        {list.isLoading && <Card padding={20}>Ładowanie…</Card>}

        {users.length > 0 && (
          <Card padding={0}>
            {users.map((u, i) => {
              const isLastAdmin = u.role === 'ADMIN' && adminCount <= 1;
              return (
                <div
                  key={u.id}
                  className="admin-user-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 14,
                    padding: '14px 22px',
                    minHeight: 64,
                    borderBottom: i < users.length - 1 ? '1px solid rgba(15,30,61,.06)' : 'none',
                    background: u.status === 'BLOCKED' ? 'rgba(208,70,60,.04)' : 'transparent',
                  }}
                >
                  <div
                    className="admin-user-header"
                    style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}
                  >
                    <Avatar nick={u.nick} size={40} />
                    <div className="admin-user-info" style={{ width: 200, flexShrink: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: 14 }}>{u.nick}</span>
                        <StatusPill status={u.status} />
                        {u.role === 'ADMIN' && <Pill color="accent">ADMIN</Pill>}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: '#5b6578',
                          marginTop: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {u.email} · {formatDate(u.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div className="admin-user-actions" style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexShrink: 0, marginLeft: 'auto' }}>
                    {u.role === 'USER' ? (
                      <IconAction
                        icon={<Ic.trophy size={16} />}
                        label="Nadaj uprawnienia administratora"
                        onClick={() => setPending({ user: u, kind: 'promote' })}
                      />
                    ) : (
                      <IconAction
                        icon={<Ic.user size={16} />}
                        label={isLastAdmin ? 'Ostatni administrator — nie można odebrać' : 'Odbierz uprawnienia administratora'}
                        onClick={() => setPending({ user: u, kind: 'demote' })}
                        disabled={isLastAdmin}
                      />
                    )}
                    {u.active ? (
                      <IconAction
                        icon={<Ic.lock size={16} />}
                        label={isLastAdmin ? 'Ostatni administrator — nie można zablokować' : 'Zablokuj użytkownika'}
                        onClick={() => setPending({ user: u, kind: 'block' })}
                        disabled={isLastAdmin}
                      />
                    ) : (
                      <IconAction
                        icon={<Ic.unlock size={16} />}
                        label="Odblokuj użytkownika"
                        onClick={() => setPending({ user: u, kind: 'unblock' })}
                      />
                    )}
                    <IconAction
                      icon={<Ic.mail size={16} />}
                      label={
                        u.status === 'PENDING_VERIFICATION'
                          ? 'Wyślij ponownie link aktywacyjny'
                          : u.status === 'BLOCKED'
                            ? 'Konto zablokowane — najpierw odblokuj'
                            : 'Konto już aktywne — link aktywacyjny niepotrzebny'
                      }
                      onClick={() => setPending({ user: u, kind: 'resend' })}
                      disabled={u.status !== 'PENDING_VERIFICATION'}
                    />
                    <IconAction
                      icon={<Ic.trash size={16} />}
                      label={isLastAdmin ? 'Ostatni administrator — nie można usunąć' : 'Usuń konto'}
                      onClick={() => setPending({ user: u, kind: 'delete' })}
                      disabled={isLastAdmin}
                      danger
                    />
                  </div>
                </div>
              );
            })}
          </Card>
        )}
      </div>

      <Modal
        open={!!pending}
        onClose={() => !isPending && setPending(null)}
        title={meta?.title ?? ''}
        footer={
          meta && (
            <>
              <Btn variant="ghost" size="md" onClick={() => setPending(null)} disabled={isPending}>
                Anuluj
              </Btn>
              <Btn
                variant={meta.danger ? 'danger' : 'accent'}
                size="md"
                icon={meta.danger ? <Ic.trash size={16} /> : <Ic.check size={16} />}
                onClick={confirmPending}
                disabled={isPending}
              >
                {isPending ? meta.busy : meta.cta}
              </Btn>
            </>
          )
        }
      >
        {pending && meta && meta.description(pending.user)}
      </Modal>
    </DesktopShell>
  );
}
